import type { DeliveryRow } from "@tdelivr/api-client/generated/experience/dispatch-console-bff.js";
import { classOfStatus, stateClass, type StateClassKey } from "@tdelivr/design-tokens";

/**
 * Which of the five absences a row carries, in the aggregate's own vocabulary.
 *
 * Read off `DeliveryRow` rather than declared a second time: EXP-008 owns the list, and a sixth
 * value arriving upstream becomes a compile error here instead of a row quietly falling through to
 * whatever this screen's `default` branch happens to say.
 */
export type CourierAbsence = NonNullable<DeliveryRow["courierAbsence"]>;

/**
 * What the active-deliveries board does to the one list EXP-008 answers with.
 *
 * The join is not here and must not be: DEL-03 publishes no courier on a delivery order and DEL-06
 * publishes no listing at all, so putting a name in the design's `Courier` column is one read per
 * row under a second scope and a third read for the name. R-09 measured that absence and R-05 says
 * where a per-row read across a service boundary belongs —
 * `services/experience/dispatch-console-bff/src/application/deliveries.ts`, which also bounds the
 * sweep and reports each layer's own verdict.
 *
 * What is left is what only a screen decides, and on this board it is more than it sounds:
 *
 *   - **The state class.** The badge hue, the glyph, the row tint and the left edge come from the
 *     design's nine-class table keyed on DEL-03's own status. A rule about pixels that no contract
 *     publishes, and the reason the aggregate hands over `status` untouched.
 *   - **The order.** The design's page says "Sorted so whatever needs a person comes first", and its
 *     own sample rows are in exactly that order. That is the same class table again, so it is
 *     applied here — over the whole bounded set, with DEL-03's stalest-first order kept as the
 *     tie-break inside each class, because the service gives a reason for that ordering and it is
 *     still the right one between two rows that need a person equally.
 *   - **The words.** The badge word, the courier cell's two lines and the four tiles' labels and
 *     footers are the design's own sentences over published fields.
 *   - **The toolbar.** Search, state filter, ordering, paging and export over the rows the one
 *     request already brought back. R-01 records that no listing on this platform publishes any of
 *     them; what makes them work here is that the aggregate bounds the set.
 */

/** The four tiles the design draws, in its order, with its words. */
export interface DeliveryTile {
  readonly label: string;
  /** The design's glyph for the tile, in its own 36px tinted square. */
  readonly glyph: string;
  /** Which state class the tile counts, and takes its badge glyph and hue from. */
  readonly classKey: StateClassKey;
  /** The design's own sentence under the count, with the number it names filled from the rows. */
  readonly foot: string;
}

/**
 * The design's tiles, label for label.
 *
 * `Needs a human`, `Failed`, `In progress` and `Waiting for courier`, with `back_hand`, `error`,
 * `sync` and `schedule` — read out of `02 Console Shell v3.dc.html`, whose ops console opens on
 * this page and draws all four.
 *
 * Every one of the four has a source, which no board on this console has managed before: DEL-03's
 * in-flight set is exactly `delivery_attempted` (human), `failed` (failed), `in_custody` and
 * `accepted` (active and idle) and `pickup_ready` (waiting), and the design's own class table maps
 * each. They are still **floors rather than totals** whenever `countsAreTotals` is false, which is
 * R-04 and is said on the board rather than hidden.
 *
 * The footers are the part that needed deciding. The design writes `Oldest 04:51`, `Oldest 11:20`,
 * `On time` and `Longest 01:05`. Three of the four are a duration over the rows in that class and
 * are computed; `On time` on the in-progress tile is not — it is a claim about whether deliveries
 * are running late, and DEL-03 publishes no promised time, no window and no due instant anywhere.
 * So that tile's footer says the same thing the other three say, in the same grammar, rather than
 * asserting punctuality nothing measures. R-17.
 */
export const DELIVERY_TILES: readonly DeliveryTile[] = [
  { label: "Needs a human", glyph: "back_hand", classKey: "human", foot: "Oldest" },
  { label: "Failed", glyph: "error", classKey: "failed", foot: "Oldest" },
  { label: "In progress", glyph: "sync", classKey: "active", foot: "Oldest" },
  { label: "Waiting for courier", glyph: "schedule", classKey: "waiting", foot: "Longest" },
];

/**
 * The state class for one row, from DEL-03's own status.
 *
 * `classOfStatus` rather than a table of this screen's own: the design's `01 Token System v2`
 * publishes the whole DEL-03 lifecycle and `extract-state-classes.mjs` reads it, so a status whose
 * class somebody changes in the design changes here without an edit. A status the table does not
 * cover returns `undefined` and the badge says so rather than borrowing a class — which is the
 * behaviour `StateBadge` already has and the reason it is reused rather than reimplemented.
 */
export function deliveryClass(row: DeliveryRow): StateClassKey | undefined {
  return classOfStatus("DEL-03", row.status);
}

/**
 * DEL-03's vocabulary in the design's words.
 *
 * The design's own `human()` — `s.replace(/_/g,' ').replace(/^./,c=>c.toUpperCase())` — applied to
 * the status exactly as DEL-03 publishes it, which is why `Delivery attempted` and `Pickup ready`
 * come out with a capital and a space. Taken from the design rather than hand-written per status,
 * because that function is what the file runs and a hand-written list would be a second opinion
 * about the same seven words.
 *
 * The raw status travels to the badge's `title`, as the design puts it on its own row badge.
 */
export function readableStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/^./, (first) => first.toUpperCase());
}

/**
 * The design's `Courier` cell, both lines of it.
 *
 * `J. Mushi` over `DRV-2291` when a courier is carrying it; `Unassigned` over `—` when nobody is,
 * and `Awaiting courier` over `—` on a row still waiting for pickup — the design's own two
 * no-courier wordings, chosen by the row's state class exactly as it chooses them (`r.k==='waiting'
 * ? 'Awaiting courier' : 'Unassigned'`).
 *
 * **Four of the five absences are not "no courier" and must not read as one.** A row whose courier
 * the aggregate could not ask about — no projection, refused, failed, or past the bound it publishes
 * as `courierLookups` — is a row where the console does not know. Drawing `Unassigned` there is the
 * failure R-09 names as worse than a blank: an operator reads a free courier and dispatches a second
 * job to somebody already carrying a parcel. So all four say `Not known`.
 *
 * **And the four are then four different cells, not one.** `Not known` four times over would tell an
 * operator that the console cannot see a courier and nothing about what to do next — refresh, ask
 * for a scope, narrow the board, or accept that this delivery has no tracking at all. So the cell's
 * second line, the mono slot the design fills with `DRV-2291`, carries a word for which of the four
 * it is: `No tracking`, `No permission`, `No answer`, `Not asked`. Four words and a glyph rather
 * than four shades of the same grey, which is design rule 2's own rule about colour applied to an
 * absence. The sentence stays on `title`, where the whole reason fits.
 */
export function courierCell(row: DeliveryRow): {
  readonly name: string;
  /** The design's mono second line: the courier's identifier, `—`, or which absence this is. */
  readonly identifier: string;
  /** Initials for the design's 30px avatar, or undefined for its dashed glyph placeholder. */
  readonly initials?: string;
  /** Why there is no courier, where that is not plain from the word. */
  readonly title?: string;
  /**
   * Which absence this is, or undefined when a courier is known.
   *
   * The screen draws `unassigned` with the design's own dashed `person` avatar, because that is the
   * cell the design drew for it. The four unknowns take a dashed `question_mark` instead — the
   * design's own idiom for "neither true yet", the same dashed treatment `01 Token System v2` gives
   * the `unknown` state class and the live map gives a driver whose job it only infers.
   */
  readonly absence?: CourierAbsence;
} {
  if (row.courierId !== null) {
    const name = row.courierName;
    return {
      name: name ?? row.courierId,
      identifier: row.courierId,
      ...(name === null
        ? // DRV-01 did not answer, or this courier's profile is not `active`. The identifier is
          // still the whole truth about who is carrying it.
          { title: "DRV-01 published no display name for this courier." }
        : { initials: initialsOf(name) }),
    };
  }

  switch (row.courierAbsence) {
    case "unassigned":
      return {
        name: classOfStatus("DEL-03", row.status) === "waiting" ? "Awaiting courier" : "Unassigned",
        identifier: "—",
        absence: "unassigned",
      };
    case "no_projection":
      return {
        name: "Not known",
        identifier: "No tracking",
        absence: "no_projection",
        title:
          "DEL-06 has no tracking for this delivery: absent, past its retention, or another tenant's. Its read answers 404 for all three without distinguishing them.",
      };
    case "forbidden":
      return {
        name: "Not known",
        identifier: "No permission",
        absence: "forbidden",
        title:
          "Reading a courier needs delivery-tracking:read, which this session does not hold. The delivery itself is shown in full.",
      };
    case "unavailable":
      return {
        name: "Not known",
        identifier: "No answer",
        absence: "unavailable",
        title: "DEL-06 did not answer for this delivery. Refreshing may fill it.",
      };
    default:
      return {
        name: "Not known",
        identifier: "Not asked",
        absence: "not_requested",
        title:
          "Beyond the courier lookups this board asks for. The couriers it did ask about are shown; this row was not asked about and is not necessarily unassigned.",
      };
  }
}

/**
 * The glyph in the dashed avatar, which is the part of the cell that is visible across a room.
 *
 * `person` for the one absence that means there is nobody — the design's own glyph, in the design's
 * own dashed circle. `question_mark` for the four that mean the console does not know, which is a
 * different fact and has to look different before it is read.
 */
export function courierGlyph(absence: CourierAbsence): string {
  return absence === "unassigned" ? "person" : "question_mark";
}

/** `J. Mushi` becomes `JM`, as the design's own `cInit` does. */
function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0] ?? "")
    .join("")
    .replace(".", "");
}

/**
 * How long a row has been in its state, as the design writes it: `04:51`.
 *
 * Minutes and seconds from DEL-03's `updatedAt` to the board's clock, zero-padded, and hours when
 * it has been that long — the design's longest sample is `32:10`, which is ambiguous between 32
 * minutes and 32 hours, so anything past an hour is drawn `h:mm:ss` rather than letting a day-old
 * delivery read as half an hour.
 *
 * `updatedAt` is the right end rather than `createdAt`: the column is headed `In state` and the
 * design's sub-line calls the board "sorted so whatever needs a person comes first", so what matters
 * is how long a delivery has been stuck where it is, not how old the order is.
 */
export function timeInState(row: DeliveryRow, now: number): string | undefined {
  const since = Date.parse(row.updatedAt);
  if (!Number.isFinite(since)) return undefined;
  const seconds = Math.max(0, Math.floor((now - since) / 1000));
  const minutes = Math.floor(seconds / 60);
  const pad = (value: number): string => String(value).padStart(2, "0");
  if (minutes < 60) return `${pad(minutes)}:${pad(seconds % 60)}`;
  return `${Math.floor(minutes / 60)}:${pad(minutes % 60)}:${pad(seconds % 60)}`;
}

/** The sort options the design's grid toolbar offers, in its order and its words. */
export type DeliveryOrder = "default" | "state" | "az" | "za";

export interface DeliveryQuery {
  /** Free text over every value on the row, as the design's grid searches. */
  readonly search: string;
  /** One of DEL-03's statuses, or undefined for every state the aggregate returned. */
  readonly status?: string | undefined;
  readonly order: DeliveryOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
  /** Now, for the `In state` column, so a search for `04:5` matches what is on screen. */
  readonly now: number;
}

export interface DeliveryPage {
  readonly rows: readonly DeliveryRow[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly DeliveryRow[];
  readonly total: number;
  readonly pageCount: number;
  /** The states present in what came back, for the toolbar's filter. */
  readonly states: readonly string[];
}

function searchableText(row: DeliveryRow, now: number): string {
  const courier = courierCell(row);
  return [
    row.orderId,
    row.status,
    readableStatus(row.status),
    row.courierId ?? "",
    row.courierName ?? "",
    courier.name,
    timeInState(row, now) ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the design's own order and not the aggregate's: whatever needs a person first, by
 * the state class's `sort`, with DEL-03's stalest-first order preserved inside each class. Both
 * halves matter — the design says so in its page sub-line, and DEL-03 says why its own ordering is
 * what it is — and a stable sort over an already-stalest-first list gives both without a second
 * comparison.
 *
 * Unlike the offer monitor's, the state filter is **over the rows rather than a query parameter**,
 * and the difference is the aggregate: EXP-008 answers DEL-03's whole in-flight set at once, so
 * every state the board can show is already here. Narrowing to one of them is a filter over what is
 * held, and the `All states` option is honestly every state on the board. What it is not is every
 * delivery ever — `delivered` and `cancelled` are terminal and outside DEL-03's in-flight set — and
 * `boundedNote` says so.
 */
export function deliveryPage(rows: readonly DeliveryRow[], query: DeliveryQuery): DeliveryPage {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (row) =>
      (query.status === undefined || row.status === query.status) &&
      (needle.length === 0 || searchableText(row, query.now).includes(needle)),
  );

  const ordered = [...matched].sort((left, right) => {
    if (query.order === "az" || query.order === "za") {
      const compared = left.orderId.localeCompare(right.orderId);
      return query.order === "az" ? compared : -compared;
    }
    // `state` and `default` are the same comparison here. The design's grid offers both and its own
    // default already puts what needs a person first, so a separate "State" order would be the
    // option that changes nothing — which is still what the design draws, and is drawn.
    return classRank(left) - classRank(right);
  });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return {
    rows: ordered.slice(from, from + query.perPage),
    all: ordered,
    total,
    pageCount,
    // Every state present in the answer, not every state DEL-03 publishes: the aggregate returns
    // the in-flight set, so offering `Delivered` would be an option that can never match a row.
    states: [...new Set(rows.map((row) => row.status))].sort(),
  };
}

/** Where a row sorts: its state class's own `sort`, with an unclassified status last. */
function classRank(row: DeliveryRow): number {
  const key = deliveryClass(row);
  return key === undefined ? Number.MAX_SAFE_INTEGER : stateClass(key).sort;
}

/** How many rows are in each tile's class, and the longest any of them has been in its state. */
export function tileCount(
  rows: readonly DeliveryRow[],
  tile: DeliveryTile,
  now: number,
): { readonly count: number; readonly foot: string } {
  const matching = rows.filter((row) => deliveryClass(row) === tile.classKey);
  if (matching.length === 0) {
    // The design draws its own `—` for a count it has no rows for, and the footer then has nothing
    // to name. One em dash rather than `Oldest 00:00`, which would read as a delivery.
    return { count: 0, foot: "—" };
  }
  const oldest = matching.reduce((worst, row) => {
    const at = Date.parse(row.updatedAt);
    return Number.isFinite(at) && at < worst ? at : worst;
  }, Number.POSITIVE_INFINITY);
  const elapsed = Number.isFinite(oldest)
    ? timeInState({ ...matching[0]!, updatedAt: new Date(oldest).toISOString() }, now)
    : undefined;
  return { count: matching.length, foot: elapsed === undefined ? "—" : `${tile.foot} ${elapsed}` };
}

/**
 * What a count on this board is allowed to look like.
 *
 * `countsAreTotals` false means every count here is a floor: DEL-03 either had more deliveries than
 * the aggregate asked for, or did not answer at all. R-04 measured that no listing on this platform
 * publishes a total, so this is the ordinary case rather than the exception.
 *
 * **The design has no treatment for a floor**, which is recorded as Q-43 rather than decided here.
 * What is drawn meanwhile is a `+` after the number and the design's own `unknown` dashed-grey
 * treatment on the tile — `01 Token System v2` defines that class as "borrows neither success nor
 * failure, because neither is true yet", which is exactly what a floor is. `7+` is read correctly
 * by anyone who has seen a page of search results, and the sentence under the board says it in
 * words as well, because a `+` is one character and an operator working a shift deserves both.
 */
export function floorMark(count: number, countsAreTotals: boolean): string {
  return countsAreTotals ? String(count) : `${count}+`;
}

/**
 * The design's footer line, `7 deliveries · 1 need a human · 1 failed`.
 *
 * Its own grammar kept, including the bare `1 need a human` the design writes. The counts are the
 * board's own classes over the rows it holds, so they agree with the tiles above by construction
 * rather than by two pieces of arithmetic that have to be kept in step.
 *
 * Every one of the three is a floor when `countsAreTotals` is false, and all three are marked: a
 * line reading `7+ deliveries · 1 need a human` would say the first number is uncertain and the
 * second is not, which is the one thing that is definitely untrue.
 */
export function countText(rows: readonly DeliveryRow[], countsAreTotals = true): string {
  const inClass = (key: StateClassKey): string =>
    floorMark(rows.filter((row) => deliveryClass(row) === key).length, countsAreTotals);
  return (
    `${floorMark(rows.length, countsAreTotals)} deliveries · ` +
    `${inClass("human")} need a human · ${inClass("failed")} failed`
  );
}

/**
 * The wall-clock time the design's stale rows are labelled with: `as of 14:02`.
 *
 * The aggregate's own `observedAt` rather than the moment the browser asked, for the reason the
 * demand queue gives about a watermark: a screen timestamping its own request calls data fresh when
 * the pipeline behind it stopped an hour ago. Local time, because an operator reads a clock on a
 * wall and the design writes `14:02 EAT`.
 */
export function wallClock(instantMs: number): string {
  const at = new Date(instantMs);
  return `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
}

/**
 * The one or two sentences this board owes an operator about what it is actually showing.
 *
 * Three different facts, and the aggregate publishes each of them rather than leaving a screen to
 * guess:
 *
 *   - **The set is DEL-03's in-flight one.** Not every delivery — `delivered` and `cancelled` are
 *     terminal and absent — and an operator who read the board as every delivery would conclude
 *     nothing was ever delivered. Always said, because it is always true.
 *   - **The counts may be floors.** `countsAreTotals` is false when DEL-03 was truncated or did not
 *     answer, and `countFloors` names which. R-04.
 *   - **The courier sweep is bounded.** `courierLookups` is how many rows it covered; past that a
 *     row's courier was not asked about, which the cell says per row and this says once.
 *
 * Returned as a list rather than one string so the board can draw them as the separate statements
 * they are, worst first.
 */
export function boundedNotes(view: {
  readonly rows: readonly DeliveryRow[];
  readonly countsAreTotals: boolean;
  readonly countFloors: readonly { readonly service: string; readonly reason: string }[];
  readonly courierLookups: number;
}): readonly string[] {
  const notes: string[] = [];

  for (const floor of view.countFloors) {
    notes.push(
      floor.reason === "truncated"
        ? `${floor.service} had more deliveries than this board asked for. Every count here is a floor rather than a total.`
        : `${floor.service} did not answer, so this board is missing every delivery it holds.`,
    );
  }

  if (view.rows.length > view.courierLookups) {
    notes.push(
      `Couriers were read for the first ${view.courierLookups} of ${view.rows.length} deliveries. The rest read "Not known" rather than unassigned.`,
    );
  }

  notes.push(
    "This is every delivery in flight: accepted, pickup ready, in custody and delivery attempted. Delivered and cancelled deliveries are not on this board.",
  );

  return notes;
}
